// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — where the Sun's bodies are, for Solar System mode
 * (docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md). Heliocentric, in the J2000 ecliptic
 * frame (x to the March equinox, z to the ecliptic's north pole), in
 * astronomical units.
 *
 * - The planets and Pluto: JPL's "Approximate Positions of the Planets"
 *   (E. M. Standish, Table 1, valid 1800–2050), Keplerian elements with
 *   linear rates. Good to well under a degree, far finer than a 5 m orrery
 *   can show. The clock is held to the table's years
 *   ({@link EPHEMERIS_MIN_MS}, {@link EPHEMERIS_MAX_MS}).
 * - Ceres: JPL Small-Body Database osculating elements at JD 2461200.5
 *   (2026-06-09), carried by its mean motion; it drifts by about a degree
 *   per decade away from that epoch, unseen at this scale.
 * - The Moon: the Astronomical Almanac's low-precision series (about 0.3°,
 *   a few hundred km), geocentric, turned from the equinox of date to J2000
 *   by the general precession. The Earth is the Earth–Moon barycentre less
 *   the Moon's share.
 *
 * JPL's tables and elements are NASA works, public domain. Checked against
 * JPL Horizons in ephemeris.test.ts.
 */

export type Vec3 = [number, number, number]

export const AU_KM = 149_597_870.7
const DEG = Math.PI / 180
const J2000_JD = 2451545.0

/** The table's years: 1800-01-01 to 2050-12-31 UTC. */
export const EPHEMERIS_MIN_MS = Date.UTC(1800, 0, 1)
export const EPHEMERIS_MAX_MS = Date.UTC(2050, 11, 31)

export function clampEphemerisTime(ms: number): number {
  return Math.min(EPHEMERIS_MAX_MS, Math.max(EPHEMERIS_MIN_MS, ms))
}

export function julianDay(ms: number): number {
  return ms / 86_400_000 + 2440587.5
}

/** Julian centuries since J2000. */
function centuries(ms: number): number {
  return (julianDay(ms) - J2000_JD) / 36525
}

/** Elements and rates per century: a (AU), e, I, L, ϖ (longitude of perihelion), Ω (degrees). */
type ElementRow = readonly [number, number, number, number, number, number]
interface PlanetTable {
  el: ElementRow
  rate: ElementRow
}

/** Standish Table 1 (1800–2050). The Earth's row is the Earth–Moon barycentre. */
const TABLE_1: Readonly<Record<string, PlanetTable>> = {
  mercury: { el: [0.38709927, 0.20563593, 7.00497902, 252.2503235, 77.45779628, 48.33076593], rate: [0.00000037, 0.00001906, -0.00594749, 149472.67411175, 0.16047689, -0.12534081] },
  venus: { el: [0.72333566, 0.00677672, 3.39467605, 181.9790995, 131.60246718, 76.67984255], rate: [0.0000039, -0.00004107, -0.0007889, 58517.81538729, 0.00268329, -0.27769418] },
  emb: { el: [1.00000261, 0.01671123, -0.00001531, 100.46457166, 102.93768193, 0.0], rate: [0.00000562, -0.00004392, -0.01294668, 35999.37244981, 0.32327364, 0.0] },
  mars: { el: [1.52371034, 0.0933941, 1.84969142, -4.55343205, -23.94362959, 49.55953891], rate: [0.00001847, 0.00007882, -0.00813131, 19140.30268499, 0.44441088, -0.29257343] },
  jupiter: { el: [5.202887, 0.04838624, 1.30439695, 34.39644051, 14.72847983, 100.47390909], rate: [-0.00011607, -0.00013253, -0.00183714, 3034.74612775, 0.21252668, 0.20469106] },
  saturn: { el: [9.53667594, 0.05386179, 2.48599187, 49.95424423, 92.59887831, 113.66242448], rate: [-0.0012506, -0.00050991, 0.00193609, 1222.49362201, -0.41897216, -0.28867794] },
  uranus: { el: [19.18916464, 0.04725744, 0.77263783, 313.23810451, 170.9542763, 74.01692503], rate: [-0.00196176, -0.00004397, -0.00242939, 428.48202785, 0.40805281, 0.04240589] },
  neptune: { el: [30.06992276, 0.00859048, 1.77004347, -55.12002969, 44.96476227, 131.78422574], rate: [0.00026291, 0.00005105, 0.00035372, 218.45945325, -0.32241464, -0.00508664] },
  pluto: { el: [39.48211675, 0.2488273, 17.14001206, 238.92903833, 224.06891629, 110.30393684], rate: [-0.00031596, 0.0000517, 0.00004818, 145.20780515, -0.04062942, -0.01183482] },
}

/** Ceres, JPL SBDB, epoch JD 2461200.5 TDB, J2000 ecliptic. */
const CERES = {
  epochJd: 2461200.5,
  a: 2.765552595034094,
  e: 0.07969229514816586,
  i: 10.58802780183462,
  node: 80.24862682043221,
  peri: 73.29421453021587,
  m0: 274.4193463761342,
  /** Degrees a day. */
  n: 0.21430445064843,
}

/** The Moon's mass over the Earth–Moon system's (the Earth is 81.3 Moons). */
const MOON_SHARE = 1 / (1 + 81.30056)

export interface OrbitElements {
  /** Semi-major axis, AU. */
  a: number
  e: number
  /** Inclination, ascending node, argument of perihelion (radians). */
  i: number
  node: number
  argPeri: number
  /** Mean anomaly, radians. */
  meanAnomaly: number
}

/** Solve Kepler's equation M = E − e sin E. */
export function eccentricAnomaly(m: number, e: number): number {
  let mm = m % (2 * Math.PI)
  if (mm > Math.PI) mm -= 2 * Math.PI
  if (mm < -Math.PI) mm += 2 * Math.PI
  let ea = e < 0.8 ? mm : Math.PI * Math.sign(mm || 1)
  for (let k = 0; k < 30; k++) {
    const d = (ea - e * Math.sin(ea) - mm) / (1 - e * Math.cos(ea))
    ea -= d
    if (Math.abs(d) < 1e-12) break
  }
  return ea
}

/** The place on an orbit for an eccentric anomaly, J2000 ecliptic AU. */
export function orbitPoint(el: OrbitElements, ea: number): Vec3 {
  const xp = el.a * (Math.cos(ea) - el.e)
  const yp = el.a * Math.sqrt(1 - el.e * el.e) * Math.sin(ea)
  const cw = Math.cos(el.argPeri)
  const sw = Math.sin(el.argPeri)
  const cO = Math.cos(el.node)
  const sO = Math.sin(el.node)
  const cI = Math.cos(el.i)
  const sI = Math.sin(el.i)
  return [
    (cw * cO - sw * sO * cI) * xp + (-sw * cO - cw * sO * cI) * yp,
    (cw * sO + sw * cO * cI) * xp + (-sw * sO + cw * cO * cI) * yp,
    sw * sI * xp + cw * sI * yp,
  ]
}

function tableElements(row: PlanetTable, ms: number): OrbitElements {
  const t = centuries(ms)
  const [a, e, inc, l, peri, node] = row.el.map((x, k) => x + row.rate[k] * t)
  return { a, e, i: inc * DEG, node: node * DEG, argPeri: (peri - node) * DEG, meanAnomaly: (l - peri) * DEG }
}

/** The elements of a heliocentric body at a time (the Earth's are the barycentre's); null for the Moon and the Sun. */
export function orbitElements(body: string, ms: number): OrbitElements | null {
  if (body === 'ceres') {
    const days = julianDay(ms) - CERES.epochJd
    return {
      a: CERES.a,
      e: CERES.e,
      i: CERES.i * DEG,
      node: CERES.node * DEG,
      argPeri: CERES.peri * DEG,
      meanAnomaly: (CERES.m0 + CERES.n * days) * DEG,
    }
  }
  const row = TABLE_1[body === 'earth' ? 'emb' : body]
  return row ? tableElements(row, ms) : null
}

/**
 * The Moon from the Earth's centre, km, J2000 ecliptic (low precision: the
 * Astronomical Almanac's series, about 0.3° and a few hundred km).
 */
export function moonGeocentricKm(ms: number): Vec3 {
  const t = centuries(ms)
  const s = (deg: number) => Math.sin(deg * DEG)
  const c = (deg: number) => Math.cos(deg * DEG)
  const lon = 218.32 + 481267.881 * t
    + 6.29 * s(135.0 + 477198.87 * t) - 1.27 * s(259.3 - 413335.36 * t)
    + 0.66 * s(235.7 + 890534.22 * t) + 0.21 * s(269.9 + 954397.74 * t)
    - 0.19 * s(357.5 + 35999.05 * t) - 0.11 * s(186.5 + 966404.03 * t)
  const lat = 5.13 * s(93.3 + 483202.02 * t) + 0.28 * s(228.2 + 960400.89 * t)
    - 0.28 * s(318.3 + 6003.15 * t) - 0.17 * s(217.6 - 407332.21 * t)
  const parallax = 0.9508 + 0.0518 * c(135.0 + 477198.87 * t) + 0.0095 * c(259.3 - 413335.36 * t)
    + 0.0078 * c(235.7 + 890534.22 * t) + 0.0028 * c(269.9 + 954397.74 * t)
  const distKm = 6378.14 / Math.sin(parallax * DEG)
  // Of date → J2000: take the general precession in longitude back out.
  const lonJ2000 = (lon - 1.396971 * t) * DEG
  const b = lat * DEG
  return [distKm * Math.cos(b) * Math.cos(lonJ2000), distKm * Math.cos(b) * Math.sin(lonJ2000), distKm * Math.sin(b)]
}

/** Heliocentric J2000 ecliptic position, AU, of a body at a time (the Sun is the origin). */
export function heliocentricAu(body: string, ms: number): Vec3 {
  if (body === 'sun') return [0, 0, 0]
  if (body === 'earth' || body === 'moon') {
    const el = orbitElements('earth', ms) as OrbitElements
    const emb = orbitPoint(el, eccentricAnomaly(el.meanAnomaly, el.e))
    const m = moonGeocentricKm(ms)
    const share = body === 'earth' ? -MOON_SHARE : 1 - MOON_SHARE
    return [emb[0] + (m[0] * share) / AU_KM, emb[1] + (m[1] * share) / AU_KM, emb[2] + (m[2] * share) / AU_KM]
  }
  const el = orbitElements(body, ms)
  if (!el) throw new Error(`no ephemeris for ${body}`)
  return orbitPoint(el, eccentricAnomaly(el.meanAnomaly, el.e))
}

/**
 * Points round a body's orbit at a time, J2000 ecliptic AU (`n` points;
 * the caller closes the loop). The Moon's are from the Earth, over one
 * sidereal month.
 */
export function orbitPath(body: string, ms: number, n = 256): Vec3[] {
  if (body === 'moon') {
    const out: Vec3[] = []
    const month = 27.321661 * 86_400_000
    for (let k = 0; k < n; k++) {
      const m = moonGeocentricKm(ms + (k / n) * month)
      out.push([m[0] / AU_KM, m[1] / AU_KM, m[2] / AU_KM])
    }
    return out
  }
  const el = orbitElements(body, ms)
  if (!el) return []
  const out: Vec3[] = []
  for (let k = 0; k < n; k++) out.push(orbitPoint(el, (k / n) * 2 * Math.PI))
  return out
}

/** Distance between two bodies, km. */
export function distanceKm(a: string, b: string, ms: number): number {
  const pa = heliocentricAu(a, ms)
  const pb = heliocentricAu(b, ms)
  return Math.hypot(pa[0] - pb[0], pa[1] - pb[1], pa[2] - pb[2]) * AU_KM
}

/** The obliquity of the ecliptic at J2000, radians. */
export const OBLIQUITY_J2000 = 23.4392911 * DEG

/** A J2000 equatorial direction (RA, Dec in degrees) in the J2000 ecliptic frame, unit vector. */
export function equatorialToEcliptic(raDeg: number, decDeg: number): Vec3 {
  const ra = raDeg * DEG
  const dec = decDeg * DEG
  const x = Math.cos(dec) * Math.cos(ra)
  const y = Math.cos(dec) * Math.sin(ra)
  const z = Math.sin(dec)
  const ce = Math.cos(OBLIQUITY_J2000)
  const se = Math.sin(OBLIQUITY_J2000)
  return [x, ce * y + se * z, -se * y + ce * z]
}
